import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { get, patch } from "../../../../../utils/api";
import {
  Checkbox,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  TextField,
  FormControl,
  InputLabel,
} from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { v4 as uuidv4 } from "uuid"; // Ensure uuid is installed

interface Field {
  id: string;
  key: string;
  type: string;
  create_status: boolean;
  edit_status: boolean;
  filter_status: boolean;
  column_status: boolean;
}

const UIPropertiesEditor = () => {
  const { id } = useParams<{ id: string }>();
  const [fields, setFields] = useState<Field[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await get(`field_settings/${id}`, {});
        setFields(
          response.data.fields.map((field: any) => ({
            ...field,
            id: field.id || uuidv4(),
          }))
        );
      } catch (error) {
        console.error("Failed to fetch field settings:", error);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchData();
    }
  }, [id]);

  const handleChange = (
    index: number,
    event: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | { name?: string; value: unknown }
    >
  ) => {
    const { name, value, type } = event.target as HTMLInputElement;
    const checked =
      type === "checkbox" ? (event.target as HTMLInputElement).checked : value;
    setFields((currentFields) =>
      currentFields.map((field, i) =>
        i === index
          ? { ...field, [name!]: type === "checkbox" ? checked : value }
          : field
      )
    );
  };


  const handleSave = async () => {
    try {
      await patch(`field_settings/${id}`, { fields });
      alert("Changes saved successfully!");
    } catch (error) {
      console.error("Failed to save changes:", error);
    }
  };

  const onDragEnd = (result: any) => {
    const { source, destination } = result;
    if (!destination) return;
    if (source.index === destination.index) return;

    setFields((prevFields) => {
      const newFields = Array.from(prevFields);
      const [reorderedItem] = newFields.splice(source.index, 1);
      newFields.splice(destination.index, 0, reorderedItem);
      return newFields;
    });
  };

  if (loading) return <div>Loading...</div>;

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Drag</TableCell>
            <TableCell>Field Key</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Create Status</TableCell>
            <TableCell>Edit Status</TableCell>
            <TableCell>Column Status</TableCell>
            <TableCell>Filter Status</TableCell>
          </TableRow>
        </TableHead>
        <Droppable droppableId="droppable-fields">
          {(provided) => (
            <TableBody ref={provided.innerRef} {...provided.droppableProps}>
              {fields.map((field, index) => (
                <Draggable key={field.id} draggableId={field.id} index={index}>
                  {(provided, snapshot) => (
                    <TableRow
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      style={{
                        ...provided.draggableProps.style,
                        backgroundColor: snapshot.isDragging
                          ? "#f4f4f4"
                          : "white",
                      }}
                    >
                      <TableCell>
                        <DragIndicatorIcon />
                      </TableCell>
                      <TableCell>
                        <span>{field.key}</span>
                      </TableCell>
                      <TableCell>
                        <span>{field.type}</span>
                      </TableCell>
                      <TableCell>
                        <Checkbox
                          name="create_status"
                          checked={field.create_status}
                          onChange={(e) => handleChange(index, e)}
                        />
                      </TableCell>
                      <TableCell>
                        <Checkbox
                          name="edit_status"
                          checked={field.edit_status}
                          onChange={(e) => handleChange(index, e)}
                        />
                      </TableCell>
                      <TableCell>
                        <Checkbox
                          name="column_status"
                          checked={field.column_status}
                          onChange={(e) => handleChange(index, e)}
                        />
                      </TableCell>
                      <TableCell>
                        <Checkbox
                          name="filter_status"
                          checked={field.filter_status}
                          onChange={(e) => handleChange(index, e)}
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </TableBody>
          )}
        </Droppable>
      </Table>
      <Button onClick={handleSave}>Save Changes</Button>
    </DragDropContext>
  );
};

export default UIPropertiesEditor;
